export type WorkspaceOS = "mac" | "windows";

export interface WorkspacePrompt {
  label: string;
  value: string;
}

export interface WorkspaceLink {
  label: string;
  href: string;
}

export interface WorkspaceStep {
  id: string;
  title: string;
  description: string;
  duration: string;
  whyItMatters: string;
  actions: string[];
  checkpoint: string;
  commands?: WorkspacePrompt[];
  note?: string;
  links?: WorkspaceLink[];
}

export interface WorkspaceInstallStep {
  title: string;
  body: string;
}

export const PROJECT_MEMORY_TEMPLATE = `# Project

## Purpose
- [What this project does and who it is for]

## Working agreements
- Explain the plan before making a large change.
- Never delete data, deploy, or push without explicit approval.
- Keep changes focused on the requested outcome.

## Commands
- Develop: [project command]
- Test: [project command]
- Build: [project command]

## Definition of done
- The requested behavior works.
- Relevant checks pass.
- The final response lists changed files, verification, and remaining risks.`;

export const META_PROMPT_STARTER = `/create-prompt How might we add a clear empty state to the customer dashboard?

The work should match the existing design system, include a useful next action, work on mobile, and be verified in the browser.`;

export const EXECUTION_BRIEF = `Implement the approved plan for the dashboard empty state.

Before editing:
1. Read CLAUDE.md and the relevant dashboard files.
2. Restate the acceptance criteria.
3. Show me the files you expect to change.

While working:
- Keep the change focused.
- Run the relevant checks.
- Stop and ask before any destructive action or major architecture change.

When finished:
- Verify the behavior at desktop and mobile sizes.
- Show me the diff summary, checks run, and any remaining risks.`;

export const VISUAL_REVIEW_PROMPT = `Compare the current page with the attached reference.

List the three most important visible gaps first. Then fix them in order.

After each pass, verify:
- hierarchy and spacing
- mobile behavior
- keyboard focus and contrast
- no horizontal overflow

Do not call it complete until you have checked the rendered page.`;

export const MCP_BOUNDARY_PROMPT = `I want to connect one MCP server for this project.

Before changing configuration:
1. Explain exactly what data and actions the server can access.
2. Recommend the narrowest scope and permissions that satisfy this task.
3. Keep credentials out of version control.
4. Show me how to disable or remove the server.

Wait for my approval before installing or authenticating it.`;

export const VERIFICATION_PROMPT = `Review the work before I commit it.

1. Show git status and summarize the diff.
2. Run the relevant tests, type checks, lint, and build commands from CLAUDE.md.
3. Verify the user-facing behavior, including mobile if this is UI work.
4. Flag unsupported claims, skipped checks, security concerns, or unrelated changes.
5. If everything passes, propose a concise commit message. Do not commit or push yet.`;

export const MAC_INSTALL_STEPS: WorkspaceInstallStep[] = [
  {
    title: "Download and unzip the pack",
    body: "Download claude-meta-prompting.zip, find it in Downloads, and double-click it. macOS creates a claude-meta-prompting folder beside the zip.",
  },
  {
    title: "Open your Claude configuration folder",
    body: "In Finder, press Command + Shift + G, type ~/.claude, and press Return.",
  },
  {
    title: "Install the commands",
    body: "Move the two .md files from the pack's commands folder into ~/.claude/commands. If commands does not exist, move the entire commands folder into ~/.claude.",
  },
  {
    title: "Install the skills",
    body: "Move the create-meta-prompts and create-plans folders into ~/.claude/skills. If skills does not exist, move the entire skills folder into ~/.claude.",
  },
  {
    title: "Confirm Claude can see them",
    body: "Return to Claude Code and type /create-prompt. If it appears in the command menu, the install worked. Restart Claude Code if this is the first skills folder you created.",
  },
];

export const WINDOWS_INSTALL_STEPS: WorkspaceInstallStep[] = [
  {
    title: "Download and extract the pack",
    body: "Download claude-meta-prompting.zip, right-click it in Downloads, choose Extract All, and open the new claude-meta-prompting folder.",
  },
  {
    title: "Open your Claude configuration folder",
    body: "In File Explorer's address bar, type %USERPROFILE%\\.claude and press Enter.",
  },
  {
    title: "Install the commands",
    body: "Move the two .md files from the pack's commands folder into the commands folder inside .claude. Create commands if it does not exist.",
  },
  {
    title: "Install the skills",
    body: "Move the create-meta-prompts and create-plans folders into the skills folder inside .claude. Create skills if it does not exist.",
  },
  {
    title: "Confirm Claude can see them",
    body: "Return to Claude Code and type /create-prompt. If it appears in the command menu, the install worked. Restart Claude Code if this is the first skills folder you created.",
  },
];

export const CLAUDE_WORKSPACE_STEPS: WorkspaceStep[] = [
  {
    id: "open-project",
    title: "Open the right project",
    description: "Choose a low-risk practice project and make sure the terminal is operating inside it.",
    duration: "5 min",
    whyItMatters:
      "Claude Code starts with the folder you launch it from. That folder defines the first trust boundary, so location is the first decision in every session.",
    actions: [
      "Choose an existing practice project or create a new folder for low-risk work. Do not begin in your entire home folder.",
      "Open Terminal on macOS or PowerShell on Windows.",
      "Use pwd on macOS or Get-Location on Windows to see where you are.",
      "Use cd to enter the project, then list the files and confirm the folder name before continuing.",
    ],
    checkpoint: "Your terminal shows the intended project folder, and you can name what Claude should and should not touch.",
    commands: [
      { label: "macOS / Linux", value: `pwd\nls\ncd path/to/your-project\npwd` },
      { label: "Windows PowerShell", value: `Get-Location\nGet-ChildItem\nSet-Location path\\to\\your-project\nGet-Location` },
    ],
    note: "If the folder contains secrets, production data, or unrelated personal files, choose a safer practice folder.",
  },
  {
    id: "git-safety-net",
    title: "Create a Git safety net",
    description: "Record a clean checkpoint before Claude changes anything.",
    duration: "7 min",
    whyItMatters:
      "Git turns every change into something you can inspect, compare, keep, or reverse. It is the workspace's audit trail, not just a tool for programmers.",
    actions: [
      "Run git status. If Git says this is not a repository, run git init once.",
      "Review the files before staging. Make sure secrets and generated folders are excluded.",
      "Create a checkpoint commit only if the starting work is ready to save.",
      "Run git status again. Know whether you are starting clean or intentionally carrying existing changes.",
    ],
    checkpoint: "You can see the starting state in git status and have a recoverable checkpoint for the practice task.",
    commands: [
      {
        label: "Checkpoint commands",
        value: `git status\n# Run this only if the folder is not already a repository:\ngit init\n\n# Review before staging:\ngit add [specific-files]\ngit commit -m "Checkpoint before Claude Code"\ngit status`,
      },
    ],
    note: "Do not use git add . until you understand everything it would stage. Specific paths make the checkpoint easier to trust.",
  },
  {
    id: "start-safely",
    title: "Start Claude Code with boundaries",
    description: "Launch Claude inside the project, inspect permissions, and begin with a read-only orientation.",
    duration: "6 min",
    whyItMatters:
      "Claude Code can read files, edit them, and run commands. A good leader gives it enough authority for the task while keeping irreversible actions behind an explicit approval.",
    actions: [
      "Run claude --version to confirm Claude Code is available, then run claude to start an interactive session.",
      "Review the workspace trust prompt carefully. Trust only the project you intentionally opened.",
      "Run /permissions. Keep destructive commands, deployments, pushes, credentials, and production access behind approval.",
      "Ask Claude to read the project and explain its current state without making changes.",
    ],
    checkpoint: "Claude can describe the project, and no file changed during the orientation pass.",
    commands: [
      { label: "Start the session", value: `claude --version\nclaude` },
      {
        label: "First message",
        value: `Read this project without changing anything.\n\nExplain:\n1. What it does\n2. How it is organized\n3. How to run and test it\n4. Any risks or missing setup you see\n\nCite the files that support your answer.`,
      },
    ],
    links: [
      { label: "Claude Code permissions", href: "https://code.claude.com/docs/en/permissions" },
      { label: "Claude Code terminal guide", href: "https://code.claude.com/docs/en/terminal-guide" },
    ],
  },
  {
    id: "project-memory",
    title: "Give the project durable instructions",
    description: "Create a concise CLAUDE.md that carries your working agreements into every session.",
    duration: "8 min",
    whyItMatters:
      "A prompt guides one task. CLAUDE.md holds the facts and rules Claude should remember every time it enters this project.",
    actions: [
      "Run /init and review what Claude proposes. If CLAUDE.md already exists, ask for improvements instead of replacing it.",
      "Keep project purpose, verified commands, architecture facts, conventions, safety boundaries, and definition of done.",
      "Remove generic advice and anything you cannot verify. Keep the file concise and resolve conflicting instructions.",
      "Run /memory and confirm the intended CLAUDE.md is loaded.",
    ],
    checkpoint: "CLAUDE.md contains specific, verifiable instructions and /memory shows that Claude loaded it.",
    commands: [{ label: "Starter CLAUDE.md", value: PROJECT_MEMORY_TEMPLATE }],
    links: [{ label: "Official CLAUDE.md guide", href: "https://code.claude.com/docs/en/memory" }],
  },
  {
    id: "install-toolkit",
    title: "Install the meta-prompting toolkit",
    description: "Add two commands and two skills that turn vague work into reusable briefs and plans.",
    duration: "7 min",
    whyItMatters:
      "The pack separates prompt design from execution. That gives you a reusable brief you can review before an implementation session spends time or changes files.",
    actions: [
      "Download the starter pack from this guide.",
      "Install the commands and skills for your operating system.",
      "Review the markdown files before trusting them. Skills are instructions with tool access, so treat them like code.",
      "Type /create-prompt in Claude Code and confirm it appears in the command menu.",
    ],
    checkpoint: "/create-prompt appears in Claude Code, and you know where the installed command and skill files live.",
    note: "The pack stores reusable prompts inside the current project's prompts folder so the work can travel with the project.",
    links: [{ label: "How Claude Code skills work", href: "https://code.claude.com/docs/en/slash-commands" }],
  },
  {
    id: "shape-the-work",
    title: "Turn the idea into an executable brief",
    description: "Use meta-prompting and plan mode to clarify outcome, scope, constraints, and verification before implementation.",
    duration: "9 min",
    whyItMatters:
      "Advanced work usually fails from an unclear finish line, not a weak model. Planning makes assumptions and verification visible while changes are still cheap.",
    actions: [
      "Run /create-prompt followed by a real task. Answer only questions that materially change the work.",
      "Read the saved prompt. Add the user outcome, in-scope files, constraints, non-goals, and evidence required for completion.",
      "Start plan mode with /plan for a large change. Ask Claude to inspect the project before proposing edits.",
      "Approve the plan only when each task has a concrete verification method.",
    ],
    checkpoint: "You have a saved brief and an approved plan whose completion can be proven, not merely claimed.",
    commands: [
      { label: "Start the meta-prompt", value: META_PROMPT_STARTER },
      { label: "Plan-mode request", value: `/plan\n\nRead the saved brief and inspect the relevant project files. Propose the smallest implementation plan that satisfies the brief. For every task, name the files, expected behavior, verification, and stop conditions. Do not edit yet.` },
    ],
    links: [{ label: "Official workflow guidance", href: "https://code.claude.com/docs/en/how-claude-code-works" }],
  },
  {
    id: "execute-and-steer",
    title: "Execute in small, reviewable loops",
    description: "Let Claude work, but keep each loop bounded by an observable result.",
    duration: "8 min",
    whyItMatters:
      "A plan is not a handoff into a black box. Short loops let you correct direction before the diff, context, and cost become hard to reason about.",
    actions: [
      "Give Claude the approved brief and let it complete one coherent slice at a time.",
      "When a decision is ambiguous, tell Claude what evidence to gather before choosing.",
      "Interrupt early when the work drifts. State what to preserve, what to change, and how you will judge the next pass.",
      "After each slice, inspect the changed files and run the narrowest relevant check.",
    ],
    checkpoint: "One meaningful slice works, its diff is understandable, and the relevant check passes before the next slice begins.",
    commands: [{ label: "Execution brief", value: EXECUTION_BRIEF }],
  },
  {
    id: "add-context",
    title: "Add visual context or one MCP connection",
    description: "Extend Claude only when the next decision needs evidence the project folder cannot provide.",
    duration: "8 min",
    whyItMatters:
      "Screenshots make visual quality inspectable. MCP can bring in external systems. Both are useful because they add evidence, but every new connection also expands context and trust.",
    actions: [
      "For visual work, paste a screenshot, drag in an image, or provide its file path. Ask Claude to compare the rendered result against it.",
      "For external data or actions, choose one approved MCP server that solves a specific gap. Start with local scope and the narrowest permissions.",
      "Keep credentials out of the repository. Review a project .mcp.json before approving it.",
      "Run /mcp to confirm connection status, test one read-only action, and document how to disable the server.",
    ],
    checkpoint: "Claude used one necessary source of external evidence, and you can explain its scope, permissions, and removal path.",
    commands: [
      { label: "Visual review", value: VISUAL_REVIEW_PROMPT },
      { label: "MCP boundary", value: MCP_BOUNDARY_PROMPT },
    ],
    note: "Do not add MCP because it is available. Add it when the task has a named information or action gap that the connection closes.",
    links: [
      { label: "Work with images", href: "https://code.claude.com/docs/en/common-workflows#work-with-images" },
      { label: "Official MCP guide", href: "https://code.claude.com/docs/en/mcp" },
    ],
  },
  {
    id: "verify-and-finish",
    title: "Verify, commit, and reset deliberately",
    description: "Close the loop with evidence, a focused commit, and a clean context decision.",
    duration: "8 min",
    whyItMatters:
      "Claude's final message is a report, not proof. The proof is the diff, passing checks, rendered behavior, and a workspace state you can explain.",
    actions: [
      "Run git status and review the diff. Remove unrelated changes and look for secrets, generated files, or unsupported claims.",
      "Run the tests, checks, build, and user-facing verification named in the brief and CLAUDE.md.",
      "Stage specific files and commit only the verified slice. Push or deploy only when that external action is explicitly intended.",
      "Use /compact when you want to continue the same task with more room. Use /clear when starting a genuinely new conversation.",
      "Use claude -p only after the manual workflow is stable and the output is bounded, reviewable, and safe to automate.",
    ],
    checkpoint: "The diff is focused, required checks pass, the commit contains only intended files, and the next session starts with an intentional context choice.",
    commands: [
      { label: "Pre-commit review", value: VERIFICATION_PROMPT },
      { label: "Bounded automation example", value: `git diff --cached | claude -p "Review this staged diff for correctness, security concerns, missing tests, and unrelated changes. Return findings only; do not modify files."` },
    ],
    links: [
      { label: "CLI reference", href: "https://code.claude.com/docs/en/cli-usage" },
      { label: "Context and commands", href: "https://code.claude.com/docs/en/commands" },
    ],
  },
];
